import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import formatPhone from '../utils/formatPhone';
import { prDaProposta } from '../utils/performanceRatio';
import { getUnoModulo, getUnoInversor, getUnoBateria, getIndices, getEmpresaAtual } from './api';
import {
  coresHex, hexParaRgb,
} from '../styles/styleUtils';

// Converte um hex de coresHex em rgb() 0-1 do pdf-lib (que não entende var()/hex).
const rgbHex = (hex) => {
  const { r, g, b } = hexParaRgb(hex);
  return rgb(r / 255, g / 255, b / 255);
};

// ============================================================
// Proposta Comercial — Sistema Off-Grid — Marcki v1
// ============================================================

const generatePdfOffGrid = async (proposta) => {
  if (!proposta) return;

  const pdfDoc  = await PDFDocument.create();
  const fontReg = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold= await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const W = 595.28; const H = 841.89;
  const ML = 40;    const MR = 40;
  const CW = W - ML - MR;

  const C = {
    azul:        rgb(0.102, 0.227, 0.431),
    azulClaro:   rgb(0.910, 0.941, 0.969),
    laranja:     rgbHex(coresHex.laranja),
    verde:       rgb(0.180, 0.490, 0.196),
    cinza:       rgb(0.412, 0.412, 0.412),
    cinzaClaro:  rgb(0.941, 0.941, 0.941),
    cinzaChumbo: rgb(0.800, 0.800, 0.800),
    bordaAzul:   rgb(0.690, 0.769, 0.847),
    preto:       rgb(0.133, 0.133, 0.133),
    branco:      rgb(1, 1, 1),
  };

  const cx      = (t, s, f) => (W - f.widthOfTextAtSize(t, s)) / 2;
  const brl     = (v) => parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const drawRect  = (pg, x, y, w, h, color) => pg.drawRectangle({ x, y, width: w, height: h, color });
  const drawBRect = (pg, x, y, w, h, fill, border) => pg.drawRectangle({ x, y, width: w, height: h, color: fill, borderColor: border || C.bordaAzul, borderWidth: 0.5 });
  const drawText  = (pg, t, x, y, s, f, color) => pg.drawText(String(t ?? ''), { x, y, size: s, font: f, color });
  const drawLine  = (pg, x1, y1, x2, y2, color = C.bordaAzul, t = 0.5) => pg.drawLine({ start:{x:x1,y:y1}, end:{x:x2,y:y2}, thickness:t, color });

  const wrapText = (text, maxW, size, font) => {
    const words = text.split(' ');
    const lines = []; let cur = '';
    words.forEach(w => {
      const test = cur ? `${cur} ${w}` : w;
      if (font.widthOfTextAtSize(test, size) <= maxW) { cur = test; }
      else { if (cur) lines.push(cur); cur = w; }
    });
    if (cur) lines.push(cur);
    return lines;
  };

  const drawParagraph = (pg, text, x, y, maxW, size, font, color, lineH) => {
    const lines = wrapText(text, maxW, size, font);
    lines.forEach(l => { drawText(pg, l, x, y, size, font, color); y -= lineH; });
    return y;
  };

  const tH = 14;
  const drawRow = (pg, cells, y) => {
    cells.forEach((c) => {
      drawBRect(pg, c.x, y - tH + 2, c.w, tH, c.fill, c.bc);
      if (c.text != null && c.text !== '') {
        const sz = c.sz || 8; const fn = c.fn || fontReg;
        let tx = c.x + 3;
        if (c.al === 'r') tx = c.x + c.w - fn.widthOfTextAtSize(String(c.text), sz) - 3;
        if (c.al === 'c') tx = c.x + (c.w - fn.widthOfTextAtSize(String(c.text), sz)) / 2;
        drawText(pg, c.text, tx, y - tH + 5, sz, fn, c.cl || C.preto);
      }
    });
    return y - tH;
  };

  const drawBarChart = (pg, data, y, availH, opts = {}) => {
    const {
      colors = [C.laranja], labelKey = 'label', valueKeys = ['v'],
      legends = [], noteTxt = '', ySteps = 5, formatY = null,
    } = opts;
    const nBars = data.length; const nSeries = valueKeys.length;
    const bW      = ((CW / nBars) / (nSeries + 1)) * 0.9;
    const axisH   = 16; const legH = legends.length > 0 ? 18 : 0;
    const noteH   = noteTxt ? 26 : 0;
    const barMaxH = availH - axisH - legH - noteH - 24;
    const allVals = data.flatMap(d => valueKeys.map(k => d[k] || 0));
    const maxVal  = Math.max(...allVals, 0.01);
    const baseY   = y - availH + noteH + legH + axisH;
    const yStepVal = maxVal / ySteps;

    for (let s = 0; s <= ySteps; s += 1) {
      const yy = baseY + (s / ySteps) * barMaxH;
      drawLine(pg, ML + 18, yy, ML + CW, yy, C.cinzaClaro, 0.4);
      const label = formatY ? formatY(s * yStepVal)
        : (yStepVal >= 100 ? Math.round(s * yStepVal).toString() : (s * yStepVal).toFixed(1));
      drawText(pg, label, ML, yy - 3, 5.5, fontReg, C.cinza);
    }
    drawLine(pg, ML + 18, baseY, ML + 18, baseY + barMaxH + 4, C.cinza, 0.8);
    drawLine(pg, ML + 18, baseY, ML + CW, baseY, C.cinza, 0.8);

    data.forEach((d, i) => {
      const xGroup = ML + 18 + i * (CW - 18) / nBars;
      valueKeys.forEach((k, si) => {
        const val = d[k] || 0;
        const h   = (val / maxVal) * barMaxH;
        const x   = xGroup + si * (bW + 2) + ((CW - 18) / nBars - nSeries * bW - (nSeries - 1) * 2) / 2;
        drawRect(pg, x, baseY, bW, h, colors[si] || C.laranja);
        if (h > 8) {
          const rot = formatY ? formatY(val) : (val >= 100 ? Math.round(val).toString() : val.toFixed(1));
          const rotW = fontBold.widthOfTextAtSize(rot, 6.5);
          drawText(pg, rot, x + (bW - rotW) / 2, baseY + h + 3, 6.5, fontBold, C.preto);
        }
      });
      const lbl = d[labelKey];
      const lblW = fontBold.widthOfTextAtSize(lbl, 7);
      drawText(pg, lbl, xGroup + ((CW - 18) / nBars - lblW) / 2, baseY - 11, 7, fontBold, C.cinza);
    });

    if (legends.length > 0) {
      let lx = ML + 18;
      legends.forEach((leg, li) => {
        drawRect(pg, lx, baseY - axisH - legH + 4, 9, 9, colors[li] || C.laranja);
        drawText(pg, leg, lx + 12, baseY - axisH - legH + 5, 7.5, fontReg, C.preto);
        lx += 9 + 12 + fontReg.widthOfTextAtSize(leg, 7.5) + 16;
      });
    }
    if (noteTxt) {
      const ny = y - availH + noteH - 4;
      drawParagraph(pg, noteTxt, ML, ny, CW, 6.5, fontReg, C.cinza, 9);
    }
    return y - availH;
  };

  const drawSecHeader = (pg, label, y, bg = C.laranja) => {
    drawRect(pg, ML, y - 4, CW, 18, bg);
    drawText(pg, label, ML + 6, y, 10, fontBold, C.branco);
    return y - 24;
  };

  // ── Imagens ───────────────────────────────────────────────
  const fetchImg = async (path) => {
    try {
      const b = await fetch(path).then(r => r.arrayBuffer());
      if (path.toLowerCase().endsWith('.png')) return await pdfDoc.embedPng(b);
      return await pdfDoc.embedJpg(b);
    } catch(e) {
      console.warn(`Falha ao incorporar imagem "${path}" no PDF:`, e);
      return null;
    }
  };

  const b64ToImg = async (base64Str) => {
    try {
      const part = base64Str.split(',')[1];
      const bin  = atob(part);
      const arr  = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
      if (base64Str.includes('image/png')) return await pdfDoc.embedPng(arr);
      return await pdfDoc.embedJpg(arr);
    } catch(e) {
      console.warn('Falha ao incorporar logo da empresa no PDF:', e);
      return null;
    }
  };

  // Logo da empresa configurada — antes fixa em /logo.png / /logoweg.png
  const empresa    = await getEmpresaAtual();
  const logoImg    = empresa?.logoPrincipal ? await b64ToImg(empresa.logoPrincipal) : null;
  const logoWegImg = empresa?.logoParceiro  ? await b64ToImg(empresa.logoParceiro)  : null;
  const imgOffGrid = await fetchImg('/Imagem Proposta Off-Grid.png');

  // Foto + dados do módulo
  let moduloImg = null;
  let moduloDados = null;
  try {
    if (proposta.kit?.modulo) {
      moduloDados = await getUnoModulo(proposta.kit.modulo);
      if (moduloDados?.foto) moduloImg = await b64ToImg(moduloDados.foto);
    }
  } catch(e) { /* sem foto */ }

  // Foto + dados do inversor
  let inversorImg = null;
  let inversorDados = null;
  try {
    if (proposta.kit?.inversor?._id) {
      inversorDados = await getUnoInversor({ _id: proposta.kit.inversor._id });
      if (inversorDados?.foto) inversorImg = await b64ToImg(inversorDados.foto);
    }
  } catch(e) { /* sem foto */ }
  if (!inversorImg) inversorImg = await fetchImg('/produtos/inversor.png');

  // Foto + dados da bateria
  let bateriaImg = null;
  let bateriaDados = null;
  try {
    if (proposta.kit?.bateria?._id) {
      bateriaDados = await getUnoBateria({ _id: proposta.kit.bateria._id });
      if (bateriaDados?.foto) bateriaImg = await b64ToImg(bateriaDados.foto);
    }
  } catch(e) { /* sem foto */ }
  if (!bateriaImg) bateriaImg = await fetchImg('/produtos/bateria.png');

  // ── Índices ───────────────────────────────────────────────
  const indices  = await getIndices().catch(() => []);
  const TARIFA   = 1.12;
  const reajuste = 0.05;
  const depPrimeiro = indices.find(i => i.nome === 'Depreciação 1º Ano (Módulo)')?.valor  || 0.007;
  const depDemais   = indices.find(i => i.nome === 'Depreciação Demais Anos (Módulo)')?.valor || 0.005;

  // ── Dados principais ──────────────────────────────────────
  const dim  = proposta.dimensionamentoOffGrid || proposta.dadosOffGrid || {};
  const pr = prDaProposta(proposta, 'offgrid');
  const kit  = proposta.kit || {};

  const MESES_ABREV = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const CHAVES_HSP  = ['hspJan','hspFev','hspMar','hspAbr','hspMai','hspJun','hspJul','hspAgo','hspSet','hspOut','hspNov','hspDez'];

  const tipoCliente  = dim.tipoCliente      || 'Residencial';
  const pctNoturno   = dim.pctNoturno       || 0.36;
  const somaDiaria   = Number(dim.somaDiaria  || 0);
  const mediaMensal  = Number(dim.mediaMensal || 0);
  const autonomia    = Number(dim.autonomia   || 0);
  const potenciaCC   = Number(kit.potenciacc  || dim.potenciaCC || 0);
  const hspMedio     = Number(dim.hspMedio    || 0);
  const fatorOrient  = dim.fatorOrientacao    || 1.0;
  const hsp          = dim.hsp               || {};
  const qtdMod       = kit.modulo?.qtde  || 1;
  const qtdInv       = kit.inversor?.qtde || 1;
  const qtdBat       = kit.bateria?.qtde  || 1;

  // ── Recalcula produção mensal a partir do HSP salvo ───────
  // (garante dados corretos independente do estado do frontend)
  const producaoMeses = CHAVES_HSP.map((chave, i) => {
    const hspMes   = hsp[chave] || 0;
    const producao = Number((potenciaCC * hspMes * 30 * pr * fatorOrient).toFixed(2));
    const economia = Number((producao * TARIFA).toFixed(2));
    return { mes: MESES_ABREV[i], producao, economia };
  });

  const ecoMedia = producaoMeses.reduce((s, m) => s + m.economia, 0) / 12;
  const ecoAnual = ecoMedia * 12;

  // ── Projeção 5 anos ───────────────────────────────────────
  const anoAtual = new Date().getFullYear();
  const analise5 = Array.from({ length: 5 }, (_, i) => ({
    ano:     anoAtual + i,
    consumo: Math.round(mediaMensal * 12),
    energia: mediaMensal * 12 * TARIFA * ((1 + reajuste) ** i),
  }));

  // ── Produção anual 25 anos ────────────────────────────────
  const producaoAnual = [];
  let prodAnoBruto = potenciaCC * hspMedio * 30 * pr * fatorOrient * 12;
  for (let i = 0; i < 25; i += 1) {
    producaoAnual.push({ label: String(anoAtual + i).slice(2), producao: Math.round(prodAnoBruto) });
    prodAnoBruto *= (1 - (i === 0 ? depPrimeiro : depDemais));
  }

  // ── Financeiro ────────────────────────────────────────────
  const valorProjeto = parseFloat(proposta.valor) || 0;
  const calcParcela  = (v, n) => {
    const t = 0.0185; const iof = 999; const cad = v * 0.034;
    return (v + iof + cad) * (t * ((1 + t) ** n)) / (((1 + t) ** n) - 1);
  };
  const payback = ecoMedia > 0 ? (valorProjeto / (ecoMedia * 12)).toFixed(1) : '–';
  const roi     = valorProjeto > 0 ? ((ecoAnual / valorProjeto) * 100).toFixed(2) : '–';

  // ── Histórico CEMIG ───────────────────────────────────────
  const anos10 = [2016,2017,2018,2019,2020,2021,2022,2023,2024,2025];
  const hTar   = anos10.map(a => indices.find(i => i.nome === `Tarifa CEMIG ${a}`)?.valor || 0);
  const anyTar = hTar.some(v => v > 0);

  // ============================================================
  // PG 1 — CAPA
  // ============================================================
  const p1 = pdfDoc.addPage([W, H]);

  if (logoImg) {
    const d = logoImg.scale(0.22);
    p1.drawImage(logoImg, { x:(W-d.width)/2, y:H-d.height-28, width:d.width, height:d.height });
  }
  if (logoWegImg) {
    const d = logoWegImg.scale(0.5);
    p1.drawImage(logoWegImg, { x:(W-d.width)/2, y:H-170-d.height, width:d.width, height:d.height });
  }
  ['Proposta Comercial','Sistema Off-Grid'].forEach((t, i) => {
    drawText(p1, t, cx(t, 16, fontBold), H - 320 - i*22, 16, fontBold, C.azul);
  });
  const lX=ML+30; const vX=lX+130; let dy=H-390;
  const dd = (l, v) => { drawText(p1,l,lX,dy,10,fontBold,C.azul); drawText(p1,v||'—',vX,dy,10,fontReg,C.preto); dy-=22; };
  dd('Cliente:',      proposta.cliente?.nome);
  dd('Telefone:',     formatPhone(proposta.cliente?.telefone||''));
  dd('Data:',         new Date().toLocaleDateString('pt-BR'));
  dd('Consultor:',    proposta.user?.nome);
  dd('Contato:',      formatPhone(proposta.user?.telefone||''));
  dy -= 11;
  dd('Nosso número:', empresa?.telefone || '(34) 98887-5093/ (34) 98854-1334');
  dd('Pós-vendas:',   empresa?.telefone || '(34) 99925-5093');
  const nomeCapa = empresa?.nome || 'MARCKI Engenharia';
  const enderecoCapa = empresa?.enderecoRodape || 'Araguari/MG';
  drawText(p1, nomeCapa,     cx(nomeCapa, 11, fontBold), 70, 11, fontBold, C.azul);
  drawText(p1, enderecoCapa, cx(enderecoCapa, 10, fontReg), 55, 10, fontReg, C.azul);

  // ============================================================
  // PG 2 — COMO FUNCIONA
  // ============================================================
  const p2 = pdfDoc.addPage([W, H]);
  let y2 = H - 30;
  y2 = drawSecHeader(p2, '1 | Como funciona o sistema off-grid fotovoltaico?', y2);
  y2 -= 6;

  // Imagem proporcional sem distorção — ocupa ~38% da altura útil
  if (imgOffGrid) {
    const dims2   = imgOffGrid.scale(1);
    const maxImgW = CW;
    const maxImgH = Math.round((H - 100) * 0.40);
    const ratio   = Math.min(maxImgW / dims2.width, maxImgH / dims2.height);
    const iW = dims2.width  * ratio;
    const iH = dims2.height * ratio;
    p2.drawImage(imgOffGrid, { x: ML + (CW - iW) / 2, y: y2 - iH, width: iW, height: iH });
    y2 -= iH + 10;
  }

  const blocoTexto2 = [
    { t: 'O sistema utiliza a energia gerada pelos módulos fotovoltaicos para alimentar o inversor off-grid, responsável por', sp: 11 },
    { t: 'gerenciar automaticamente a distribuição de energia para as cargas da instalação.', sp: 16 },
    { t: 'Durante o dia, a energia produzida pelos módulos é utilizada para alimentar os equipamentos conectados e,', sp: 11 },
    { t: 'simultaneamente, carregar o banco de baterias. Quando a geração solar não é suficiente ou durante o período', sp: 11 },
    { t: 'noturno, as baterias assumem automaticamente o fornecimento de energia, garantindo continuidade e segurança.', sp: 16 },
    { t: 'Dessa forma, o sistema opera de maneira 100% autônoma, armazenando a energia excedente para utilização nos', sp: 11 },
    { t: 'momentos em que ela for mais necessária, proporcionando independência da rede elétrica convencional.', sp: 16 },
    { t: 'Além de gerenciar o fluxo de energia, o inversor off-grid incorpora proteções contra:', sp: 11 },
    { t: '- Sobrecarga;  - Curto-circuito;  - Subtensão;  - Sobretensão;  - Sobretemperatura;  - Descarga excessiva.', sp: 16 },
    { t: 'Esses recursos aumentam a vida útil dos equipamentos, reduzem custos com manutenção e proporcionam maior', sp: 11 },
    { t: 'confiabilidade para toda a instalação.', sp: 11 },
  ];
  const caixaH2 = blocoTexto2.reduce((s, l) => s + l.sp, 0) + 20;
  drawBRect(p2, ML, y2 - caixaH2, CW, caixaH2, C.cinzaClaro, C.bordaAzul);
  let ytxt2 = y2 - 10;
  blocoTexto2.forEach(l => { drawText(p2, l.t, ML+8, ytxt2, 8, fontBold, C.preto); ytxt2 -= l.sp; });
  y2 -= caixaH2 + 10;

  if (y2 > 100) {
    y2 = drawSecHeader(p2, 'Benefícios do Sistema', y2, C.laranja);
    y2 -= 4;
    [
      ['Autonomia Total',       'Independência completa da rede elétrica convencional.'],
      ['Segurança e Confiança', 'Energia garantida 24h por dia, mesmo em locais remotos.'],
      ['Economia a Longo Prazo','Redução de custos com geradores e combustíveis.'],
      ['Sustentabilidade',      'Energia limpa que preserva o meio ambiente.'],
      ['Tecnologia WEG',        'Equipamentos de alta eficiência e durabilidade.'],
    ].forEach(([t, d]) => {
      const tw = fontBold.widthOfTextAtSize(`• ${t}: `, 8);
      drawText(p2, `• ${t}: `, ML, y2, 8, fontBold, C.laranja);
      drawText(p2, d, ML + tw + 2, y2, 8, fontReg, C.preto);
      y2 -= 13;
    });
  }

  // ============================================================
  // PG 3 — ANALISANDO AS CARGAS
  // ============================================================
  const p3 = pdfDoc.addPage([W, H]);
  let y3 = H - 30;
  y3 = drawSecHeader(p3, '2 | Analisando as cargas da instalação', y3);
  y3 -= 6;
  y3 = drawParagraph(p3,
    'Nossa empresa entende a importância de avaliar cada cenário de forma particular e personalizada, pois, em se tratando de um sistema isolado da rede da distribuidora, é essencial que a autonomia do sistema seja calculada de forma correta e efetiva, pois o menor erro pode ocasionar a indisponibilidade de energia até o início do dia seguinte.',
    ML, y3, CW, 8, fontBold, C.preto, 11);
  y3 -= 8;

  const cA=CW*0.55; const cB=CW*0.30; const cC=CW*0.15;
  [
    ['Tipo de Instalação:',             tipoCliente,                          ''],
    ['Total de Cargas:',                somaDiaria.toFixed(2),                'kW'],
    ['Energia Consumida:',              mediaMensal.toFixed(2),               'kWh/dia'],
    ['Autonomia:',                      autonomia.toFixed(2),                 'horas'],
    ['Percentual de Consumo Noturno:',  `${(pctNoturno*100).toFixed(0)}%`,   ''],
    ['Tensão de Operação:',             `${kit.inversor?.tensao || '220'}`,   'V'],
  ].forEach(([l, v, u]) => {
    y3 = drawRow(p3, [
      {x:ML,       w:cA, fill:C.cinzaChumbo, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:ML+cA,    w:cB, fill:C.branco,      text:v, fn:fontBold, cl:C.preto, bc:C.preto, al:'r'},
      {x:ML+cA+cB, w:cC, fill:C.branco,      text:u, fn:fontReg,  cl:C.cinza, bc:C.preto},
    ], y3);
  });
  y3 -= 6;
  drawText(p3,'*As informações acima foram obtidas por meio do cálculo de consumo dos equipamentos fornecidos pelo cliente.', ML, y3, 6.5, fontReg, C.cinza);
  y3 -= 10;

  const c1=CW*0.28; const c2=CW*0.22; const c3=CW*0.25; const c4=CW*0.25;
  y3 = drawRow(p3, [
    {x:ML,          w:c1, fill:C.cinzaChumbo, text:'Cidade de Instalação:', fn:fontBold, cl:C.cinza, bc:C.preto},
    {x:ML+c1,       w:c2, fill:C.branco,      text:proposta.localinstalacao?.cidade?.nome||'', fn:fontBold, cl:C.preto, bc:C.preto},
    {x:ML+c1+c2,    w:c3, fill:C.cinzaChumbo, text:'Estado:', fn:fontBold, cl:C.cinza, bc:C.preto},
    {x:ML+c1+c2+c3, w:c4, fill:C.branco,      text:proposta.localinstalacao?.estado?.sigla||'', fn:fontBold, cl:C.preto, bc:C.preto},
  ], y3);
  y3 = drawRow(p3, [
    {x:ML,          w:c1, fill:C.cinzaChumbo, text:'Valor do KWh:', fn:fontBold, cl:C.cinza, bc:C.preto},
    {x:ML+c1,       w:c2, fill:C.branco,      text:`R$ ${TARIFA.toFixed(2)}`, fn:fontBold, cl:C.preto, bc:C.preto},
    {x:ML+c1+c2,    w:c3, fill:C.cinzaChumbo, text:'Reajuste Anual Est.:', fn:fontBold, cl:C.cinza, bc:C.preto},
    {x:ML+c1+c2+c3, w:c4, fill:C.branco,      text:'5%', fn:fontBold, cl:C.preto, bc:C.preto},
  ], y3);
  y3 -= 10;

  // Tabela 5 anos
  drawRect(p3, ML, y3-24, CW, 26, C.laranja);
  drawText(p3,'Uma pergunta importante? Você já parou para pensar quanto gastaria de energia elétrica nos', ML+4, y3-8,  7, fontBold, C.branco);
  drawText(p3,'próximos 5 anos sem o sistema off-grid? Vamos analisar!', ML+4, y3-18, 7, fontBold, C.branco);
  y3 -= 26;
  const a1=CW*0.15; const a2=CW*0.25; const a3=CW*0.15; const a4=CW*0.45;
  y3 = drawRow(p3,[
    {x:ML,          w:a1, fill:C.cinzaChumbo, text:'Ano',              fn:fontBold, cl:C.preto, al:'c'},
    {x:ML+a1,       w:a2, fill:C.cinzaChumbo, text:'Consumo Anual',   fn:fontBold, cl:C.preto, al:'c'},
    {x:ML+a1+a2,    w:a3, fill:C.cinzaChumbo, text:'Reajuste',        fn:fontBold, cl:C.preto, al:'c'},
    {x:ML+a1+a2+a3, w:a4, fill:C.cinzaChumbo, text:'Energia Elétrica',fn:fontBold, cl:C.preto, al:'c'},
  ], y3);
  analise5.forEach((r, i) => {
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    y3 = drawRow(p3,[
      {x:ML,          w:a1, fill, text:r.ano,             fn:fontBold, cl:C.preto,   al:'c'},
      {x:ML+a1,       w:a2, fill, text:`${r.consumo} kWh`,al:'c'},
      {x:ML+a1+a2,    w:a3, fill, text:'5%',              al:'c'},
      {x:ML+a1+a2+a3, w:a4, fill, text:brl(r.energia),   fn:fontBold, cl:C.laranja, al:'r'},
    ], y3);
  });
  y3 -= 12;
  drawText(p3,'Uma conta que não para de subir...', cx('Uma conta que não para de subir...', 9, fontBold), y3, 9, fontBold, C.preto);
  y3 -= 12;
  y3 = drawParagraph(p3,
    'Além do aumento natural da tarifa de energia, que acompanha a inflação e demais oscilações de mercado, a dependência da rede elétrica convencional não traz previsibilidade para o seu orçamento. O sistema off-grid garante independência energética total, com custo fixo e retorno garantido ao longo dos anos.',
    ML, y3, CW, 7.5, fontReg, C.preto, 10);
  y3 -= 8;

  // Gráfico histórico CEMIG
  if (anyTar && y3 > 100) {
    drawText(p3,'Evolução da Tarifa CEMIG (R$/kWh)', cx('Evolução da Tarifa CEMIG (R$/kWh)', 9, fontBold), y3, 9, fontBold, C.preto);
    y3 -= 14;
    const gLegH3 = 22; const gH3 = y3 - 40 - gLegH3;
    const maxT3 = Math.max(...hTar, 0.01);
    const baseY3 = 40 + gLegH3;
    for (let s = 0; s <= 5; s += 1) {
      const yy = baseY3 + (s/5)*gH3;
      drawLine(p3, ML+18, yy, ML+CW, yy, C.cinzaClaro, 0.4);
      drawText(p3, `R$${((s/5)*maxT3).toFixed(2)}`, ML, yy-3, 5, fontReg, C.cinza);
    }
    drawLine(p3, ML+18, baseY3, ML+18, baseY3+gH3+4, C.cinza, 0.8);
    drawLine(p3, ML+18, baseY3, ML+CW, baseY3, C.cinza, 0.8);
    anos10.forEach((ano, i) => {
      const x   = ML + 18 + i * (CW-18) / anos10.length;
      const bw3 = (CW-18) / anos10.length;
      const bW3 = bw3 * 0.55;
      const ht  = hTar[i]>0 ? (hTar[i]/maxT3)*gH3 : 0;
      drawRect(p3, x+2, baseY3, bW3, ht, C.laranja);
      if (hTar[i]>0) drawText(p3, `R$${hTar[i].toFixed(2)}`, x+1, baseY3+ht+3, 6.5, fontBold, C.laranja);
      const aW = fontBold.widthOfTextAtSize(String(ano), 7);
      drawText(p3, String(ano), x+(bw3-aW)/2, baseY3-13, 7, fontBold, C.cinza);
    });
    drawRect(p3, ML+18, baseY3-24, 9, 9, C.laranja);
    drawText(p3,'Tarifa CEMIG', ML+32, baseY3-23, 8, fontReg, C.preto);
  }

  // ============================================================
  // PG 4 — SOLUÇÃO PROPOSTA
  // ============================================================
  const p4 = pdfDoc.addPage([W, H]);
  let y4 = H - 30;
  y4 = drawSecHeader(p4, '2 | Solução Proposta', y4);
  y4 -= 6;
  y4 = drawParagraph(p4,
    'De posse das informações das cargas do empreendimento, bem como as horas de uso estimadas por dia, foi possível dimensionar o sistema fotovoltaico e o banco de baterias necessário para atender de forma segura a demanda energética do local de instalação.',
    ML, y4, CW, 8, fontReg, C.preto, 11);
  y4 -= 8;
  y4 = drawSecHeader(p4, 'Resumo do sistema:', y4, C.laranja);
  y4 -= 4;

  const rL=CW*0.55; const rV=CW*0.30; const rU=CW*0.15;
  [
    ['Quantidade de módulos:',   `${qtdMod}`,                                            ''],
    ['Potência Total (kWp):',    `${potenciaCC.toFixed(2)}`,                             'kWp'],
    ['Potência Inversor:',       `${kit.inversor?.potencia || '–'}`,                     'kW'],
    ['Energia Armazenada:',      `${kit.autonomia || kit.bateria?.autonomia || '–'}`,    'kWh'],
    ['Potência Bateria:',        `${kit.potenciabateria || kit.bateria?.potencia || '–'}`,'kW'],
    ['Autonomia (horas):',       autonomia.toFixed(2),                                   'horas'],
    ['Média Mensal:',            mediaMensal.toFixed(2),                                 'kWh/mês'],
    ['Economia Est. Mensal:',    brl(ecoMedia),                                          ''],
  ].forEach(([l, v, u]) => {
    y4 = drawRow(p4, [
      {x:ML,      w:rL, fill:C.cinzaChumbo, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:ML+rL,   w:rV, fill:C.branco,      text:v, fn:fontBold, cl:C.preto, bc:C.preto, al:'r'},
      {x:ML+rL+rV,w:rU, fill:C.branco,      text:u, fn:fontReg,  cl:C.cinza, bc:C.preto},
    ], y4);
  });
  y4 -= 10;

  // Gráfico geração × economia mensal
  y4 = drawSecHeader(p4, 'Resumo da sua Geração:', y4, C.laranja);
  y4 -= 8;
  drawText(p4,'Geração x Economia Energia', cx('Geração x Economia Energia', 10, fontBold), y4, 10, fontBold, C.preto);
  y4 -= 14;

  // Reserva 48 pontos para a nota de rodapé antes de calcular altura disponível
  const availH4 = y4 - 50;
  drawBarChart(p4, producaoMeses.map(m => ({ label: m.mes, g: m.producao, e: m.economia })), y4, availH4, {
    colors: [C.laranja, C.verde],
    labelKey: 'label',
    valueKeys: ['g', 'e'],
    legends: ['Geração (kWh)', 'Economia (R$)'],
    ySteps: 5,
    noteTxt: '* A estimativa de geração apresentada foi calculada em cenário ideal. Valores podem variar conforme sombreamento e posicionamento dos módulos.',
  });
  y4 = 28;

  // ============================================================
  // PG 5 — DETALHAMENTO DOS PRODUTOS
  // ============================================================
  const p5 = pdfDoc.addPage([W, H]);
  let y5 = H - 30;
  y5 = drawSecHeader(p5, '3| Detalhamento dos Produtos', y5);
  y5 -= 6;
  y5 = drawParagraph(p5,
    'A seguir, detalharemos as informações referentes aos equipamentos deste orçamento. Informamos que não trabalhamos com estoque físico, então a disponibilidade dos equipamentos pode sofrer alterações. Neste caso, eventuais alterações em potência e modelo dos equipamentos orçados serão previamente comunicadas.',
    ML, y5, CW, 8, fontReg, C.preto, 11);
  y5 -= 8;

  const hW5 = CW/2 - 4; const invX5 = ML + hW5 + 8;
  drawRect(p5, ML,    y5-4, hW5, 14, C.laranja); drawText(p5,'Módulo Fotovoltaico', ML+4,    y5, 9, fontBold, C.branco);
  drawRect(p5, invX5, y5-4, hW5, 14, C.laranja); drawText(p5,'Inversor Off-Grid',   invX5+4, y5, 9, fontBold, C.branco);
  y5 -= 18;

  const mW1=hW5*0.55; const mW2=hW5*0.45;
  const yModStart = y5;
  [
    ['Potência:',           `${kit.modulo?.potencia||moduloDados?.potencia||'–'} Wp`],
    ['Modelo:',              kit.modulo?.modelo||moduloDados?.modelo||'–'],
    ['Fabricante:',          moduloDados?.marca||kit.modulo?.marca||'WEG'],
    ['Garantia (Geração):',  moduloDados?.garantiaeficiencia ? `${moduloDados.garantiaeficiencia} Anos` : '–'],
    ['Garantia (Fábrica):',  moduloDados?.garantiafabrica ? `${moduloDados.garantiafabrica} Anos` : `${kit.modulo?.garantiafabrica||'–'} Anos`],
  ].forEach(([l, v]) => {
    y5 = drawRow(p5,[
      {x:ML,     w:mW1, fill:C.cinzaChumbo, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:ML+mW1, w:mW2, fill:C.branco,      text:v, fn:fontBold, cl:C.preto, bc:C.preto},
    ], y5);
  });

  let yInv5 = yModStart;
  [
    ['Potência:',   `${kit.inversor?.potencia||inversorDados?.potencia||'–'} kW`],
    ['Modelo:',      kit.inversor?.modelo||inversorDados?.modelo||'–'],
    ['Fabricante:',  inversorDados?.fabricante||kit.inversor?.fabricante||'WEG'],
    ['Garantia:',   `${inversorDados?.garantia||kit.inversor?.garantia||'–'} Anos`],
    ['Tensão:',     `${inversorDados?.tensao||kit.inversor?.tensao||'220'} V`],
  ].forEach(([l, v]) => {
    yInv5 = drawRow(p5,[
      {x:invX5,      w:mW1, fill:C.cinzaChumbo, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:invX5+mW1,  w:mW2, fill:C.branco,      text:v, fn:fontBold, cl:C.preto, bc:C.preto},
    ], yInv5);
  });

  // Imagens módulo + inversor lado a lado
  const imgBottom5 = Math.min(y5, yInv5) - 8;
  const imgH5 = 100;
  if (moduloImg) {
    const sc = moduloImg.scaleToFit(hW5 * 0.7, imgH5);
    p5.drawImage(moduloImg, { x: ML + (hW5 - sc.width)/2, y: imgBottom5 - sc.height, width: sc.width, height: sc.height });
  }
  if (inversorImg) {
    const sc = inversorImg.scaleToFit(hW5 * 0.7, imgH5);
    p5.drawImage(inversorImg, { x: invX5 + (hW5 - sc.width)/2, y: imgBottom5 - sc.height, width: sc.width, height: sc.height });
  }
  drawText(p5, '*imagens meramente ilustrativas', ML, imgBottom5 - imgH5 - 6, 7, fontReg, C.cinza);

  // Bateria — tabela sem coluna extra, imagem abaixo
  let yBat5 = imgBottom5 - imgH5 - 20;
  drawRect(p5, ML, yBat5-4, CW, 14, C.laranja);
  drawText(p5, 'Bateria', ML+4, yBat5, 9, fontBold, C.branco);
  yBat5 -= 18;

  const batW1 = CW*0.5; const batW2 = CW*0.5;
  [
    ['Potência:',           `${kit.bateria?.potencia||bateriaDados?.potencia||'–'} kW`],
    ['Energia Disponível:', `${kit.bateria?.autonomia||bateriaDados?.autonomia||'–'} kWh`],
    ['Garantia:',           `${bateriaDados?.garantia||kit.bateria?.garantia||'–'} anos`],
    ['Vida útil:',          `${bateriaDados?.vidarutil||kit.bateria?.vidarutil||'6000'} ciclos`],
  ].forEach(([l, v]) => {
    yBat5 = drawRow(p5,[
      {x:ML,       w:batW1, fill:C.cinzaChumbo, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:ML+batW1, w:batW2, fill:C.branco,      text:v, fn:fontBold, cl:C.preto, bc:C.preto},
    ], yBat5);
  });

  // Imagem da bateria abaixo da tabela, centralizada e em tamanho maior
  if (bateriaImg) {
    const batImgH = Math.min(yBat5 - 20, 130);
    const sc = bateriaImg.scaleToFit(CW * 0.4, batImgH);
    p5.drawImage(bateriaImg, {
      x: ML + (CW - sc.width) / 2,
      y: yBat5 - sc.height - 8,
      width: sc.width,
      height: sc.height,
    });
  }

  // ============================================================
  // PG 6 — GRÁFICO ANUAL + ANÁLISE FINANCEIRA + RESUMO
  // ============================================================
  const p6 = pdfDoc.addPage([W, H]);
  let y6 = H - 30;

  // ── Gráfico anual 25 anos — topo da página ────────────────
  drawText(p6,'Projeção de Geração Anual (25 anos)', cx('Projeção de Geração Anual (25 anos)', 9, fontBold), y6, 9, fontBold, C.preto);
  y6 -= 12;
  const availH6 = Math.round((H - 30) * 0.33); // ~33% da página
  drawBarChart(p6, producaoAnual, y6, availH6, {
    colors: [C.verde], labelKey: 'label', valueKeys: ['producao'],
    legends: ['Produção (kWh)'], ySteps: 4,
    noteTxt: '* Projeção considerando depreciação anual dos módulos. Valores reais podem variar conforme condições locais.',
    formatY: (v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : Math.round(v).toString(),
  });
  y6 -= availH6 + 14;

  // ── Header análise financeira ─────────────────────────────
  y6 = drawSecHeader(p6, '4 | Análise Financeira e Resumo do Investimento', y6);
  y6 -= 8;

  // ── Retorno + Simulação lado a lado ──────────────────────
  const halfC = CW / 2 - 4; const invX6 = ML + halfC + 8;

  drawRect(p6, ML, y6-4, halfC, 14, C.laranja);
  drawText(p6,'Retorno do Investimento', ML+4, y6, 9, fontBold, C.branco);
  let yLeft = y6 - 18;
  [
    ['Payback Recurso Próprio:', `${payback} anos`],
    ['ROI:',                     `${roi}%`],
    ['Economia Mensal Est.:',    brl(ecoMedia)],
    ['Economia Anual Est.:',     brl(ecoAnual)],
    ['Valor do Investimento:',   brl(valorProjeto)],
    ['Prazo de Entrega:',        '90 dias (confirmação + assinatura)'],
    ['Validade da Proposta:',    '10 dias'],
  ].forEach(([l, v], i) => {
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    yLeft = drawRow(p6,[
      {x:ML,           w:halfC*0.6, fill, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:ML+halfC*0.6, w:halfC*0.4, fill, text:v, fn:fontBold, cl:C.preto, bc:C.preto, al:'r'},
    ], yLeft);
  });

  drawRect(p6, invX6, y6-4, halfC, 14, C.laranja);
  drawText(p6,'Simulação de Financiamento', invX6+4, y6, 9, fontBold, C.branco);
  let yRight = y6 - 18;
  drawBRect(p6, invX6, yRight-12, halfC, 12, C.azulClaro, C.bordaAzul);
  drawText(p6,'Prazo de Carência Sugerido: 90 dias corridos', invX6+4, yRight-8, 7, fontReg, C.preto);
  yRight -= 14;
  [
    ['Simulação em 36x', brl(calcParcela(valorProjeto, 36))],
    ['Simulação em 48x', brl(calcParcela(valorProjeto, 48))],
    ['Simulação em 60x', brl(calcParcela(valorProjeto, 60))],
    ['Simulação em 72x', brl(calcParcela(valorProjeto, 72))],
  ].forEach(([l, v], i) => {
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    yRight = drawRow(p6,[
      {x:invX6,           w:halfC*0.6, fill, text:l, fn:fontBold, cl:C.cinza,   bc:C.preto},
      {x:invX6+halfC*0.6, w:halfC*0.4, fill, text:v, fn:fontBold, cl:C.laranja, bc:C.preto, al:'r'},
    ], yRight);
  });
  drawBRect(p6, invX6, yRight-20, halfC, 18, C.cinzaClaro, C.bordaAzul);
  drawText(p6,'* Taxa de juros 1,85% a.m. IOF e Tarifa de cadastro inclusos.', invX6+3, yRight-10, 6, fontReg, C.cinza);
  drawText(p6,'** Sujeito à aprovação de crédito.', invX6+3, yRight-18, 6, fontReg, C.cinza);

  y6 = Math.min(yLeft, yRight) - 14;

  // ── Resumo de componentes ─────────────────────────────────
  y6 = drawSecHeader(p6, 'Resumo dos Componentes', y6, C.laranja);
  y6 -= 4;
  const cp1=CW*0.7; const cp2=CW*0.3;
  y6 = drawRow(p6,[
    {x:ML,    w:cp1, fill:C.cinzaChumbo, text:'Componentes', fn:fontBold, cl:C.preto, bc:C.preto},
    {x:ML+cp1,w:cp2, fill:C.cinzaChumbo, text:'Quantidade',  fn:fontBold, cl:C.preto, bc:C.preto, al:'c'},
  ], y6);
  [
    [`Módulo Monocristalino (${kit.modulo?.potencia||'–'}Wp):`, `${qtdMod}`],
    [`Inversor Off-Grid (${kit.inversor?.potencia||'–'}kW):`,   `${qtdInv}`],
    [`Bateria (${kit.bateria?.autonomia||'–'}kWh):`,            `${qtdBat}`],
    ['Estrutura de Fixação:',                                   '1'],
    ['Kit Material Elétrico:',                                  '1'],
    ['Frete:',                                                  'Incluso'],
    ['Serviço de Instalação, Projetos e ART:',                  '1'],
    ['Acompanhamento Remoto e Monitoramento:',                  'Incluso'],
  ].forEach(([l, v], i) => {
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    y6 = drawRow(p6,[
      {x:ML,    w:cp1, fill, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:ML+cp1,w:cp2, fill, text:v, fn:fontBold, cl:C.preto, bc:C.preto, al:'c'},
    ], y6);
  });
  y6 -= 10;

  // ── Valor total ───────────────────────────────────────────
  drawRect(p6, ML, y6-4, CW, 18, C.laranja);
  drawText(p6, `Valor total do Projeto: ${brl(valorProjeto)}`, ML+6, y6, 12, fontBold, C.branco);
  y6 -= 36;

  // ── Rodapé ────────────────────────────────────────────────
  drawLine(p6, ML,         y6+10, ML+CW*0.44, y6+10, C.preto, 0.8);
  drawLine(p6, ML+CW*0.56, y6+10, ML+CW,      y6+10, C.preto, 0.8);
  drawText(p6, empresa?.razaoSocial || 'Novacki & Andrade Engenharia LTDA', ML, y6,    8, fontBold, C.preto);
  drawText(p6, `CNPJ: ${empresa?.cnpj || '38.143.553/0001-76'}`, ML, y6-11, 8, fontReg,  C.preto);
  drawText(p6,`Cliente: ${proposta.cliente?.nome||''}`,   ML+CW*0.56, y6,    8, fontReg, C.preto);
  drawText(p6,`CPF/CNPJ: ${proposta.cliente?.cpf||''}`,  ML+CW*0.56, y6-11, 8, fontReg, C.preto);
  const rodapeFinalOffGrid = `${empresa?.nome || 'MARCKI Engenharia'} — ${empresa?.enderecoRodape || 'Araguari/MG'}`;
  drawText(p6, rodapeFinalOffGrid, cx(rodapeFinalOffGrid, 8, fontReg), 10, 8, fontReg, C.cinza);

  // ── Export ────────────────────────────────────────────────
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href  = url;
  link.download = `Proposta_OffGrid_${(proposta.cliente?.nome||'Cliente').replace(/ /g,'_')}_Marcki.pdf`;
  link.click();
  URL.revokeObjectURL(url);
};

export default generatePdfOffGrid;
